import * as React from 'react';
import { Form, Divider, Button, Table } from '@beehive/components';
import { isBlank, financial, prettyBytes } from 'utils';
import PropTypes from 'prop-types';
import messages from 'messages/Workbench/ProjectDetail';
import _ from 'lodash';
import NewCooperate from '@mpc/common/src/features/NewCooperate';
import ShowLabel from 'components/ShowLabel';
import NewPlanInfoTable from './newPlanInfoTable';
import {
  FORM_ITEM_ORDERS,
  FORM_ITEM_PROPS_BASE,
  FULL_COL_PROPS_KEY,
} from './utils';
import DescriptionsFormItem from '@mpc/common/src/components/DescriptionsFormItem';
import TextDisplay from '@mpc/common/src/components/TextDisplay';

export interface PlanInfoProps {
  prefixCls: string;
  planInfoConfig?: any;
  inPlanning?: boolean;
}

const FULL_COL_PROPS = {
  itemColProps: { span: 24 },
  itemLabelColProps: { span: 4 },
  itemInputColProps: { span: 20 },
};
export const HALF_COL_PROPS = {
  itemColProps: { span: 12 },
  itemLabelColProps: { span: 8 },
  itemInputColProps: { span: 16 },
};
const FORM_PROPS = {
  mode: 'show',
  isLabelVertical: false,
  ...FULL_COL_PROPS,
};
const FORM_BASIC_ITEMS = [
  { key: 'translated_plan_type', label: 'plan_type' },
  { key: 'project_planners', label: 'project_planners', ...HALF_COL_PROPS },
  // { key: 'budget', label: 'budget_short', ...HALF_COL_PROPS },
];
const FORM_ATTACHS_ITEM_PLAN = { key: 'plan_attachs', label: 'plan_attachs' };
const TABLE_COLUMNS = [
  { key: 'index', title: 'index', align: 'left', width: 80 },
  { key: 'platform', title: 'superior_resource_id', align: 'left', width: 130 },
  { key: 'account', title: 'account', align: 'left', width: 334 },
  { key: 'remark', title: 'remark', align: 'left', width: 334 },
];

class PlanInfo extends React.Component<PlanInfoProps, any> {
  public static contextTypes = {
    intl: PropTypes.object,
    detail: PropTypes.object,
    getAttachementDownloadLink: PropTypes.func,
  };

  private getTableData = () => {
    const { intl, detail = {} } = this.context;
    const { project_plan_info: planInfo = {} } = detail;
    const { cooperate_accounts = [] } = planInfo;
    return cooperate_accounts.map((account: any, index: number) => {
      const { superior_resource_ships = [] } = account;
      return {
        id: account.id,
        index: index + 1,
        platform: superior_resource_ships
          .map((i: any) => i.name)
          .join(intl.formatMessage(messages.comma_alias)),
        account: account.account,
        remark: account.remark,
      };
    });
  };

  private getTableColumns = () => {
    const { intl } = this.context;
    return TABLE_COLUMNS.map((item: any) => {
      return {
        ...item,
        title: intl.formatMessage(messages[item.title]),
        render: (v: any, r: any, i: number) => {
          return isBlank(v) ? '--' : v;
        },
        dataIndex: item.key,
      };
    });
  };

  private getFormBaiscItemValue = (item: any = {}) => {
    const { intl, detail = {} } = this.context;
    const { project_plan_info: planInfo = {} } = detail;
    const { key: itemKey, custom_field = false, value: val } = item;
    let value = custom_field ? val : planInfo[itemKey];
    if (itemKey === 'project_planners') {
      value = value
        .map((i: any) => i.user_name)
        .join(intl.formatMessage(messages.comma_alias));
    }
    if (itemKey === 'budget') {
      value = isBlank(value) ? '--' : financial(value);
    }
    return isBlank(value) ? '--' : value;
  };

  private getFormAttachsItemFields = () => {
    const { detail = {} } = this.context;
    const { project_plan_info: planInfo = {} } = detail;
    const {
      plan_attachs = [],
      purchase_attachs = [],
      reference_attachs = [],
    } = planInfo;
    const fields = [{ ...FORM_ATTACHS_ITEM_PLAN, attachs: plan_attachs }];
    if (!isBlank(purchase_attachs)) {
      fields.push({
        key: 'purchase_attachs',
        label: 'purchase_attachs',
        attachs: purchase_attachs,
      });
    }
    if (!isBlank(reference_attachs)) {
      fields.push({
        key: 'reference_attachs',
        label: 'reference_attachs',
        attachs: reference_attachs,
      });
    }
    return fields;
  };

  getFormBaiscItemFields = () => {
    const { detail = {} } = this.context;
    const { planInfoConfig } = this.props;
    const { project_plan_info: planInfo = {} } = detail;
    const { custom_values = [] } = planInfo;
    const customFields = custom_values.reduce((prev: any, curr: any) => {
      const {
        value,
        custom_field: { identify: key, name: label },
      } = curr;
      let field = { key, label, value, custom_field: true, ...HALF_COL_PROPS };
      if (FULL_COL_PROPS_KEY.includes(key)) {
        field = { ...field, ...FULL_COL_PROPS };
      }
      prev.push(field);
      return prev;
    }, []);
    const sortedCustomFields = _.sortBy(customFields, (i: any) => {
      return FORM_ITEM_ORDERS.indexOf(i.key);
    });
    if (planInfoConfig && planInfoConfig.getAppendFormBasicItems) {
      return planInfoConfig.getAppendFormBasicItems(
        FORM_BASIC_ITEMS.concat(sortedCustomFields),
        detail
      );
    }
    return FORM_BASIC_ITEMS.concat(sortedCustomFields);
  };

  public render() {
    const { intl, getAttachementDownloadLink, detail } = this.context;
    const { prefixCls, inPlanning } = this.props;

    const className = `${prefixCls}-plan-info`;
    const formBaiscItems = this.getFormBaiscItemFields();
    const formAttachsItems = this.getFormAttachsItemFields();
    const tableColumns = this.getTableColumns();
    const tableData = this.getTableData();

    const isStructuredQuote = detail?.structured && detail?.structured_version > 0;

    return (
      <div className={className}>
        <Form {...FORM_PROPS} className="basics">
          {formBaiscItems.map((item: any) => {
            const { custom_field = false } = item;
            const label = `${
              custom_field
                ? item.label
                : intl.formatMessage(messages[item.label])
            }`;
            // @ts-ignore
            const labelTip = FORM_ITEM_PROPS_BASE[item.key || '']?.labelTip;
            const showLabelTip =
              typeof labelTip === 'function'
                ? labelTip({
                    keys: item.key && [item.key],
                    demand_id: detail?.demand_plan_info?.demand_id,
                  })
                : labelTip;

            return (
              <Form.Item
                {...item}
                key={item.key}
                label={<ShowLabel text={label} labelTip={showLabelTip} colon />}
              >
                <div>{this.getFormBaiscItemValue(item)}</div>
              </Form.Item>
            );
          })}
        </Form>
        <Divider />
        <Form {...FORM_PROPS} className="attachs">
          {formAttachsItems.map((item: any) => {
            const { attachs = [] } = item;
            const label = `${intl.formatMessage(
              messages[item.label]
            )}${intl.formatMessage(messages.colon)}`;
            return (
              <Form.Item {...item} key={item.key} label={label}>
                <div>
                  {isBlank(attachs)
                    ? '--'
                    : attachs.map((file: any, index: number) => {
                        const { id, blob = {} } = file;
                        const { filename = '', byte_size = 0 } = blob;
                        return (
                          <div key={id} className="file-item">
                            {`${index + 1}${intl.formatMessage(
                              messages.comma_alias
                            )}${filename}`}
                            <span className="file-size">
                              {`${intl.formatMessage(messages.bracketsWrap, {
                                content: prettyBytes(byte_size),
                              })}`}
                            </span>
                            <Button
                              link
                              href={getAttachementDownloadLink(id)}
                              target="_blank"
                            >
                              {intl.formatMessage(messages.download)}
                            </Button>
                          </div>
                        );
                      })}
                </div>
              </Form.Item>
            );
          })}
        </Form>
        {
          detail?.plan_type !== 2 && <>
            {isStructuredQuote && (
              <DescriptionsFormItem
                className='m-b-20'
                items={[{
                  formItemKey: 'accept_desc',
                  label: '验收通过要求',
                  componentClass: 'textarea',
                  required: false,
                  view: true,
                  span: 2,
                  elementProps: {
                    maxLength: 1000,
                    style: { width: '100%' },
                    showComponent: () => {
                      return <TextDisplay value={detail?.demand_plan_info?.accept_desc} />
                    },
                  },
                }]}
              />
            )}
            {detail.structured ? (
              <NewCooperate
                hasTitle={false}
                showBindDataKpi={false}
                detail={detail}
                view={true}
                planInfo={detail.project_plan_info}
                category_id={detail.l3_category_id}
                inPlanning={inPlanning}
                kpiTableProps={{
                  colKeys: [
                    {
                      key: 'actual_value',
                      show: false,
                    },
                    {
                      key: 'result',
                      show: false,
                    },
                  ],
                }}
              />
            ) : (
              <Table
                columns={tableColumns}
                data={tableData}
                size="small"
                rowKey="id"
              />
            )}
          </>
        }
        
      </div>
    );
  }
}

export default PlanInfo;
